import React, { useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Icon } from "@iconify/react";

/**
 * Renders a template body inside a sandboxed <iframe> rather than via
 * dangerouslySetInnerHTML.
 *
 * Why an iframe: with sandbox="allow-same-origin" (and NO "allow-scripts")
 * the browser itself refuses to execute any <script> in the document, so no
 * manual sanitizing is needed, and the template's full markup (styles,
 * tables, head, doctype) is rendered as a real, isolated document, the way
 * an email client would show it.
 *
 * Input handling (users paste templates from emails or other places):
 *   1. Full documents (<html> / <!doctype>) are handed over untouched.
 *   2. HTML fragments are wrapped in a minimal default document.
 *   3. Plain text (no HTML tags) is converted to HTML first: escaped,
 *      blank lines -> paragraphs, single newlines -> <br>, and URLs /
 *      markdown links -> anchors. Without this, HTML collapses newlines
 *      and everything renders as one run-on block.
 */

const looksLikeHtml = (s) => /<!doctype|<!--|<\/?[a-z][\s\S]*?>/i.test(s || "");

const decodeEntities = (s) =>
  s
    .replace(/&nbsp;/gi, " ")
    .replace(/&lt;/gi, "<")
    .replace(/&gt;/gi, ">")
    .replace(/&quot;/gi, '"')
    .replace(/&#0?39;|&apos;/gi, "'")
    .replace(/&amp;/gi, "&");

// Structural tags that only exist in a real email document.
const REAL_DOC_TAGS = /<(?:!doctype|html|head|body|table)[\s>]/i;
// The same tags, but escaped (&lt;html ...&gt;).
const ESCAPED_DOC_TAGS = /&lt;\s*(?:!doctype|html|head|body|table)[\s&>]/i;

// Recovers real HTML when the template's *source code* was pasted into a
// rich-text editor (e.g. Quill) or HTML-escaped by a storage layer. Quill
// stores pasted source as <p>&lt;!DOCTYPE html&gt;</p><p>&lt;html ...</p>:
// it contains real <p> tags, so it "looks like" HTML, yet it only renders
// as visible code. Unwrap the editor's paragraphs and decode the entities.
const decodeIfEscaped = (s) => {
  const input = s || "";
  if (REAL_DOC_TAGS.test(input) || !ESCAPED_DOC_TAGS.test(input)) return input;

  const unwrapped = input
    .replace(/<\/p>\s*<p[^>]*>/gi, "\n")
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<\/?[a-z][^>]*>/gi, ""); // editor wrappers (p, span, a, ...)

  return decodeEntities(unwrapped);
};

const escapeHtml = (s) =>
  s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

// Plain text -> HTML paragraphs, preserving line breaks and linking URLs.
const plainTextToHtml = (text) => {
  const paragraphs = text
    .replace(/\r\n?/g, "\n")
    .trim()
    .split(/\n{2,}/)
    .map((para) => {
      let out = escapeHtml(para);

      // [label](url) markdown links
      out = out.replace(
        /\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g,
        '<a href="$2">$1</a>',
      );
      // bare URLs not already inside an href or link text
      out = out.replace(
        /(^|[^">])(https?:\/\/[^\s<]+)/g,
        '$1<a href="$2">$2</a>',
      );

      return `<p>${out.replace(/\n/g, "<br>")}</p>`;
    });
  return paragraphs.join("");
};

// Styles for plain-text templates only (we generate the markup ourselves).
const TEXT_STYLES = `
  html, body { margin: 0; padding: 0; }
  body {
    font-family: -apple-system, "Segoe UI", Roboto, Arial, sans-serif;
    font-size: 14px; line-height: 1.6; color: #222;
    padding: 12px; overflow-wrap: break-word;
  }
  p { margin: 0 0 12px; }
  a { color: #1a73e8; }
`;

// Styles for real HTML templates. Deliberately minimal: email layouts are
// built from nested tables with their own widths, so global rules such as
// box-sizing, img max-width or overflow-wrap:anywhere would collapse the
// columns and distort the design. Render it the way an email client does.
const HTML_STYLES = `
  html, body { margin: 0; padding: 0; }
  body { padding: 8px; }
`;

const wrapDoc = (content, styles) => `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <base target="_blank">
  <style>${styles}</style>
</head>
<body>${content}</body>
</html>`;

const buildSrcDoc = (raw) => {
  const input = decodeIfEscaped(raw).trim();

  // A full document from an email builder: hand it over untouched.
  if (/<html[\s>]|<!doctype/i.test(input)) return input;

  // HTML fragment (e.g. a copied table): minimal wrapper only.
  if (looksLikeHtml(input)) return wrapDoc(input, HTML_STYLES);

  // Plain text: convert to paragraphs / links.
  return wrapDoc(plainTextToHtml(input), TEXT_STYLES);
};

const EmailPreview = ({
  body,
  htmlBody,
  label = "Email Preview",
  variant = "compact",
}) => {
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);

  if (!body) return null;

  // Render through the iframe whenever the flag says HTML OR the content
  // itself is HTML (raw or escaped), so a missing/false flag can't cause
  // the markup to show up as text.
  const renderAsHtml =
    Boolean(htmlBody) || looksLikeHtml(decodeIfEscaped(body));

  const templateContent = (className = "") =>
    renderAsHtml ? (
      <EmailHtmlFrame html={body} className={className} title={label} />
    ) : (
      <p
        className={`email-preview__text ${className}`}
        style={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}
      >
        {body}
      </p>
    );

  return (
    <>
      <div className={`email-preview email-preview--${variant}`}>
        <div className="email-preview__header">
          <span className="email-preview__label">
            <Icon icon="mdi:email-outline" />
            {label}
          </span>
          <button
            type="button"
            className="email-preview__toggle"
            onClick={() => setIsPreviewOpen(true)}
            aria-label="Open full email preview"
            title="Open full preview"
          >
            <Icon icon="mdi:eye-outline" />
          </button>
        </div>

        <div className="email-preview__body">{templateContent()}</div>
      </div>

      {isPreviewOpen &&
        createPortal(
          <div
            className="email-preview-modal"
            role="dialog"
            aria-modal="true"
            aria-label={`${label} full preview`}
            onMouseDown={() => setIsPreviewOpen(false)}
          >
            <div
              className="email-preview-modal__panel"
              onMouseDown={(event) => event.stopPropagation()}
            >
              <div className="email-preview-modal__header">
                <span>
                  <Icon icon="mdi:email-outline" /> {label}
                </span>
                <button
                  type="button"
                  onClick={() => setIsPreviewOpen(false)}
                  aria-label="Close full email preview"
                >
                  <Icon icon="mdi:close" />
                </button>
              </div>
              <div className="email-preview-modal__body">
                {templateContent("email-preview-modal__content")}
              </div>
            </div>
          </div>,
          document.body,
        )}
    </>
  );
};

// Separated so its own hooks (ref + height state) don't get re-created by
// the parent's re-renders any more than necessary.
const EmailHtmlFrame = ({ html, className, title }) => {
  const iframeRef = useRef(null);
  const [height, setHeight] = useState(null);

  const srcDoc = useMemo(() => buildSrcDoc(html), [html]);

  const handleLoad = () => {
    const doc = iframeRef.current?.contentDocument;
    if (doc?.body) {
      // Measure after load; fonts/images can shift layout slightly.
      setHeight(
        Math.max(doc.documentElement.scrollHeight, doc.body.scrollHeight),
      );
    }
  };

  return (
    <iframe
      ref={iframeRef}
      title={title}
      className={`email-preview__iframe ${className}`}
      srcDoc={srcDoc}
      onLoad={handleLoad}
      // No "allow-scripts": scripts can never run. "allow-popups" only lets
      // links with target="_blank" open in a new tab.
      sandbox="allow-same-origin allow-popups allow-popups-to-escape-sandbox"
      style={{
        display: "block",
        width: "100%",
        border: 0,
        height: height ? `${height}px` : undefined,
      }}
    />
  );
};

export default EmailPreview;
